import { useQuery } from '@tanstack/react-query'
import { Shield, Car, Home, FileText, FolderOpen, TrendingUp, Building2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { api } from '@/lib/api'
import { CATEGORIES } from '@/lib/categories'
import { formatCurrency } from '@/lib/utils'
import ItemCard from '@/components/ItemCard'
import type { Category } from '@/types'

const CAT_ICONS = { Shield, Car, Home, FileText, FolderOpen, TrendingUp, Building2 }
const CAT_ROUTES: Record<Category, string> = {
  vehicle: '/vehicles', home: '/home', property: '/property',
  shares: '/shares', tax: '/tax', insurance: '/insurance', other: '/other',
}

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: api.dashboard,
    refetchInterval: 60_000,
  })

  const now = new Date()
  const greeting =
    now.getHours() < 12 ? 'Good morning' :
    now.getHours() < 17 ? 'Good afternoon' : 'Good evening'

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-20 bg-white rounded-xl border border-slate-200 animate-pulse" />
        ))}
      </div>
    )
  }

  const urgent = [
    ...(data?.expired ?? []),
    ...(data?.expiring_soon ?? []),
  ].slice(0, 10)

  const totalAnnual = Object.values(data?.annual_costs ?? {}).reduce((a, b) => a + b, 0)

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <div>
        <h2 className="text-xl font-semibold text-slate-900">{greeting}</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          {now.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
      </div>

      {/* Annual cost summary */}
      {totalAnnual > 0 && (
        <div className="bg-brand-600 text-white rounded-xl p-4">
          <div className="flex items-center gap-2 text-brand-100 text-sm mb-1">
            <TrendingUp size={14} />
            Annual recurring costs
          </div>
          <p className="text-2xl font-bold">{formatCurrency(totalAnnual)}</p>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {Object.entries(data?.annual_costs ?? {}).filter(([, v]) => v > 0).map(([cat, val]) => (
              <span key={cat} className="text-xs text-brand-200">
                {CATEGORIES[cat as Category]?.label}: {formatCurrency(val)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Urgent / expiring */}
      {urgent.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">
            Needs Attention
          </h3>
          <div className="space-y-2">
            {urgent.map(item => <ItemCard key={item.id} item={item} compact />)}
          </div>
        </section>
      )}

      {urgent.length === 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-700">
          All good — nothing expiring in the next 90 days.
        </div>
      )}

      {/* Category shortcuts */}
      <section>
        <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">
          Categories
        </h3>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {(['vehicle','home','property','shares','tax','insurance'] as Category[]).map(cat => {
            const config = CATEGORIES[cat]
            const Icon = CAT_ICONS[config.icon as keyof typeof CAT_ICONS] ?? FolderOpen
            const count = data?.counts[cat] ?? 0
            return (
              <Link
                key={cat}
                to={CAT_ROUTES[cat]}
                className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3 hover:shadow-sm active:scale-[0.99] transition-all"
              >
                <div className={`rounded-lg p-2 ${config.bgColor}`}>
                  <Icon size={16} className={config.color} />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-800">{config.label}</p>
                  <p className="text-xs text-slate-400">{count} item{count !== 1 ? 's' : ''}</p>
                </div>
              </Link>
            )
          })}
        </div>
      </section>
    </div>
  )
}
